import React, { FC } from 'react';
import { ModalContent, AlertRoundIcon, Asset, Button } from '@clearcover/atlas';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { Table } from 'Shared/components';
import { getPaymentPlanDisplayName } from '../utils';
import styles from './styles.module.css';

const GENERIC_MONTHLY_PAYMENTS_DISCLAIMER =
  'Installment fees are charged to cover the cost of payment processing and bill generation.';

const MONTHLY_PAYMENTS_DISCLAIMER =
  'All payment plan options except "Pay in full" include a {installmentFee} installment fee on future payments. ';

const replacePlaceholders = (
  template: string,
  values: { [key: string]: string }
) => {
  return template.replace(/\{(\w+)\}/g, (_, key) => values[key] || '');
};

export function isGreaterThanZero(value: string | null | undefined): boolean {
  if (!value) return false;

  const numericString = value.replace(/[^0-9.]/g, '');

  const number = parseFloat(numericString);

  return !isNaN(number) && number > 0;
}

export const PaymentScheduleModalContent: FC<{
  paymentPlan: PaymentPlanType;
  closeModal: () => void;
}> = ({ paymentPlan, closeModal }) => {
  const isFlexiblePlan = paymentPlan.paymentPlanEnum === 'flexible';

  const rows = paymentPlan.paymentSchedule.map((schedule, index) => {
    return {
      key: index,
      className: styles.installmentRow,
      data: [
        <span key={`payment-schedule-date-${schedule.date}`}>
          {schedule.date}
        </span>,
        <span key={`payment-amount-amount-${schedule.date}`}>
          {schedule.amount}
        </span>,
      ],
    };
  });

  const buildFeesDisclaimer = () => {
    // No disclaimer needed for Pay in Full plans
    if (paymentPlan.paymentFrequencyIdentifier === 'in_full') {
      return '';
    }

    const installmentFee = paymentPlan.installmentFeeAmount!;

    const disclaimer = replacePlaceholders(MONTHLY_PAYMENTS_DISCLAIMER, {
      installmentFee,
    });

    return `${disclaimer} ${GENERIC_MONTHLY_PAYMENTS_DISCLAIMER}`;
  };

  const renderPaymentPlanDisplayName = () => {
    return (
      <div className={styles.modalSubHeader}>
        <Asset
          icon={AlertRoundIcon}
          className={styles.alertIcon}
          description="Form Error Alert Icon"
          height={16}
          width={16}
        />
        {getPaymentPlanDisplayName(paymentPlan)}
      </div>
    );
  };

  return (
    <ModalContent>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>Payment schedule</div>

        {!isFlexiblePlan && renderPaymentPlanDisplayName()}

        <div className={styles.modalContent}>
          <Table
            headers={['Due date', 'Amount due']}
            headerClassName={styles.installmentHeader}
            columnClassNames={[
              styles.installmentDateColumn,
              styles.installmentAmountColumn,
            ]}
            footerEl={<></>}
            columnWidths={[1, '100px']}
            className={styles.table}
            striped={false}
            rows={rows}
            name="payment-frequency"
          />
          {isGreaterThanZero(paymentPlan.installmentFeeAmount) && (
            <p className={styles.feesDisclaimer}>{buildFeesDisclaimer()}</p>
          )}
          <div className={styles.controls}>
            <Button variant="primary" size="sm" onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </ModalContent>
  );
};
